/**
 * Redirects a user to a new page using the nextjs router.
 */

import Router from 'next/router';
import type { ParsedUrlQuery } from 'querystring';
import { addServerMessage } from '~/jobiqo/services/messages';
import { Message } from '~/jobiqo/models/app';
import { getCurrentFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { getServerFrontendConfig } from '~/jobiqo/lib/frontendConfig';

const getRedirectAppConfig = (ctx: any = {}) =>
  (ctx?.req || ctx?.res
    ? getServerFrontendConfig(ctx)
    : getCurrentFrontendConfig()
  ).APP;

const redirect = (
  target: string,
  ctx: any = {},
  message: Message | null = null,
  code: number | null = null,
) => {
  const appConfig = getRedirectAppConfig(ctx);
  const hasMultipleLocales = appConfig.LOCALES?.length > 1;
  const defaultLocale = appConfig.DEFAULT_LOCALE;
  const basePath = appConfig.BASE_PATH;

  if (ctx.res) {
    if (!ctx.res.finished) {
      // server
      // 303: "See other"
      const fullTarget = `${basePath ? basePath : ''}${target}`;
      ctx.res.writeHead(code || 303, {
        Location: hasMultipleLocales
          ? `/${ctx.locale ?? defaultLocale}${fullTarget}`
          : fullTarget,
      });
      ctx.res.end();
    }
  } else {
    if (message) {
      addServerMessage(message);
    }
    // In the browser, we just pretend like this never even happened ;)
    Router.replace(target);
  }
};

/**
 * Checks if a status code is a redirect code.
 *
 * @param code
 *   The status code to check.
 * @returns
 *   True if the code is a redirect code, false otherwise.
 */
export const isRedirectCode = (code: number | undefined): boolean => {
  return code === 301 || code === 302 || code === 307 || code === 308;
};

/**
 * Checks if a status code is a permanent redirect code (301).
 *
 * @param code The status code to check.
 * @returns True if the code is 301, false otherwise.
 */
export const isPermanentRedirectCode = (code: number | undefined): boolean => {
  return code === 301;
};

/**
 * Creates a redirect link for when inside getServerSideProps
 *
 * Prefixes the URL with the ctx.locale to handle multilingual websites.
 *
 * @param ctx
 *  The page context.
 * @param location
 *   The location to redirect to.
 * @returns
 *   The URL destination to redirect to.
 */
export const gsspRedirect = (ctx, location) => {
  const appConfig = getRedirectAppConfig(ctx);
  const hasMultipleLocales = appConfig.LOCALES?.length > 1;
  return hasMultipleLocales ? `/${ctx.locale}/${location}` : location;
};

/**
 * Validates a redirect URL to prevent open redirect attacks.
 *
 * Only allows relative paths (starting with a single "/").
 * Rejects absolute URLs, protocol-relative URLs ("//evil.com"),
 * and backslash-based bypasses ("\/evil.com").
 *
 * @param url - The URL to validate.
 * @param fallback - Fallback path if the URL is invalid.
 * @returns A safe relative URL or the fallback.
 */
export const sanitizeRedirectUrl = (
  url: string,
  fallback = '/dashboard',
): string => {
  if (!url || typeof url !== 'string') {
    return fallback;
  }
  // Normalize backslashes (some browsers treat \ as /).
  const normalized = url.replace(/\\/g, '/');
  // Must start with exactly one / (not // which is protocol-relative).
  if (!normalized.startsWith('/') || normalized.startsWith('//')) {
    return fallback;
  }
  return url;
};

/**
 * Builds an internal redirect URL with an encoded destination parameter.
 *
 * @param url - The URL to add the destination parameter to.
 * @param destination - The destination value to encode.
 * @returns The URL with an encoded destination query parameter.
 */
export const buildRedirectUrlWithDestination = (
  url: string,
  destination: string,
): string => {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}destination=${encodeURIComponent(destination)}`;
};

/**
 * Appends query parameters parsed alongside destination back to the redirect URL.
 *
 * This protects legacy/unencoded links such as
 * `/user/login?destination=/jobs?page=2&jobId=28`, where Next.js parses `jobId`
 * as a login-page query parameter even though it belongs to the destination.
 *
 * @param url - The redirect URL to append query parameters to.
 * @param query - The parsed router query.
 * @param excludedParams - Query keys that should not be appended.
 * @returns The redirect URL with preserved query parameters.
 */
export const appendQueryParamsToRedirectUrl = (
  url: string,
  query: ParsedUrlQuery,
  excludedParams = ['destination', 'dispatch'],
): string => {
  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (excludedParams.includes(key) || typeof value === 'undefined') {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, item));
      return;
    }

    params.append(key, value);
  });

  const preservedParams = params.toString();
  if (!preservedParams) {
    return url;
  }

  const hashIndex = url.indexOf('#');
  const urlWithoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
  const separator = urlWithoutHash.includes('?') ? '&' : '?';

  return `${urlWithoutHash}${separator}${preservedParams}${hash}`;
};

/**
 * Removes a query parameter from a relative URL while preserving all other query
 * parameters and hash fragments.
 *
 * @param url - The URL to remove a query parameter from.
 * @param paramName - The query parameter name to remove.
 * @returns The URL without the query parameter.
 */
export const removeQueryParamFromUrl = (
  url: string,
  paramName: string,
): string => {
  const hashIndex = url.indexOf('#');
  const urlWithoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
  const queryIndex = urlWithoutHash.indexOf('?');

  if (queryIndex < 0) {
    return url;
  }

  const pathname = urlWithoutHash.slice(0, queryIndex);
  const query = urlWithoutHash.slice(queryIndex + 1);
  const params = new URLSearchParams(query);
  params.delete(paramName);

  const queryString = params.toString();
  return `${pathname}${queryString ? `?${queryString}` : ''}${hash}`;
};

/**
 * Returns a shallow copy of a parsed query object without a query parameter.
 *
 * @param query - The parsed query object.
 * @param paramName - The query parameter name to omit.
 * @returns A query object without the omitted parameter.
 */
export const omitQueryParam = (
  query: ParsedUrlQuery,
  paramName: string,
): ParsedUrlQuery => {
  const { [paramName]: _omittedParam, ...queryWithoutParam } = query;
  return queryWithoutParam;
};

export default redirect;
